import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

// Keep the approved placeholder until the customer support URL is provided.
export const QUERY_BUILDER_SUPPORT = '[link]';

export type QueryBuilderErrorKind =
	| 'errorTitle'
	| 'ideTitle'
	| 'query'
	| 'queryWithId'
	| 'siteContext'
	| 'siteContextWithId'
	| 'sites'
	| 'sitesWithId'
	| 'noSites'
	| 'schema'
	| 'savedLoad'
	| 'savedAppMismatch'
	| 'savedSave'
	| 'forgeApps'
	| 'noApps'
	| 'timeout'
	| 'rateLimit'
	| 'signIn'
	| 'noQueries'
	| 'noQueriesTitle'
	| 'noQueriesFiltered'
	| 'buildQuery'
	| 'queryName'
	| 'forgeApp'
	| 'invalidApp';

const queryBuilderErrorMessages: Record<QueryBuilderErrorKind, MessageDescriptor> = defineMessages({
	errorTitle: {
		id: 'dtac.queryBuilder.errors.errorTitle.ai-non-final',
		defaultMessage: 'Error',
		description:
			'Query Builder errorTitle feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	ideTitle: {
		id: 'dtac.queryBuilder.errors.ideTitle.ai-non-final',
		defaultMessage: 'IDE error',
		description:
			'Query Builder ideTitle feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	query: {
		id: 'dtac.queryBuilder.errors.query.ai-non-final',
		defaultMessage: 'There was an issue running this query, please reach out to {support}.',
		description:
			'Query Builder query feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	queryWithId: {
		id: 'dtac.queryBuilder.errors.queryWithId.ai-non-final',
		defaultMessage:
			'There was an issue running this query, please reach out to {support} with this ID: {requestId}',
		description:
			'Query Builder queryWithId feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	siteContext: {
		id: 'dtac.queryBuilder.errors.siteContext.ai-non-final',
		defaultMessage:
			'We weren’t able to find that data with the provided site. Please make sure you have the right site selected. If issues continue, please reach out to {support}.',
		description:
			'Query Builder siteContext feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	siteContextWithId: {
		id: 'dtac.queryBuilder.errors.siteContextWithId.ai-non-final',
		defaultMessage:
			'We weren’t able to find that data with the provided site. Please make sure you have the right site selected. If issues continue, please reach out to {support} with this ID: {requestId}',
		description:
			'Query Builder siteContextWithId feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	sites: {
		id: 'dtac.queryBuilder.errors.sites.ai-non-final',
		defaultMessage: 'There was an issue logging you into your site. Please reach out to {support}.',
		description:
			'Query Builder sites feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	sitesWithId: {
		id: 'dtac.queryBuilder.errors.sitesWithId.ai-non-final',
		defaultMessage:
			'There was an issue logging you into your site. Please reach out to {support} with this ID: {requestId}',
		description:
			'Query Builder sitesWithId feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	noSites: {
		id: 'dtac.queryBuilder.errors.noSites.ai-non-final',
		defaultMessage:
			'This account isn’t associated with an Atlassian site. Please reach out to {support} if this is in error.',
		description:
			'Query Builder noSites feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	schema: {
		id: 'dtac.queryBuilder.errors.schema.ai-non-final',
		defaultMessage:
			'We couldn’t load the information needed to prepare your query. Please reach out to {support} for support.',
		description:
			'Query Builder schema feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	savedLoad: {
		id: 'dtac.queryBuilder.errors.savedLoad.ai-non-final',
		defaultMessage:
			'We couldn’t load your saved queries. Please reach out to {support} for support.',
		description:
			'Query Builder savedLoad feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	savedAppMismatch: {
		id: 'dtac.queryBuilder.errors.savedAppMismatch.ai-non-final',
		defaultMessage:
			'We couldn’t load your saved queries. Please try again or reach out to {support} for support.',
		description:
			'Query Builder savedAppMismatch feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	savedSave: {
		id: 'dtac.queryBuilder.errors.savedSave.ai-non-final',
		defaultMessage:
			'We couldn’t save your query. Please try again or reach out to {support} for support.',
		description:
			'Query Builder savedSave feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	forgeApps: {
		id: 'dtac.queryBuilder.errors.forgeApps.ai-non-final',
		defaultMessage:
			'We couldn’t load your Forge apps. Please try again or reach out to {support} for support.',
		description:
			'Query Builder forgeApps feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	noApps: {
		id: 'dtac.queryBuilder.errors.noApps.ai-non-final',
		defaultMessage:
			'No Forge apps are available for this account. Create a Forge app or ask an app owner to add you as a contributor.',
		description:
			'Query Builder noApps feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	timeout: {
		id: 'dtac.queryBuilder.errors.timeout.ai-non-final',
		defaultMessage:
			'Your query timed out. Please try a simpler query, or reach out to {support} if the issue persists.',
		description:
			'Query Builder timeout feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	rateLimit: {
		id: 'dtac.queryBuilder.errors.rateLimit.ai-non-final',
		defaultMessage: 'Too many requests. Wait a moment and try again.',
		description:
			'Query Builder rateLimit feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	signIn: {
		id: 'dtac.queryBuilder.errors.signIn.ai-non-final',
		defaultMessage: 'Sign in to run your query.',
		description:
			'Query Builder signIn feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	noQueries: {
		id: 'dtac.queryBuilder.errors.noQueries.ai-non-final',
		defaultMessage: 'No saved queries yet. Build a query, then save it to a Forge app.',
		description:
			'Query Builder noQueries feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	noQueriesTitle: {
		id: 'dtac.queryBuilder.errors.noQueriesTitle.ai-non-final',
		defaultMessage: 'No saved queries yet',
		description:
			'Query Builder noQueriesTitle feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	noQueriesFiltered: {
		id: 'dtac.queryBuilder.errors.noQueriesFiltered.ai-non-final',
		defaultMessage: 'No saved queries found',
		description:
			'Heading in the saved queries view when saved queries exist but none match the current search or filters.',
	},
	buildQuery: {
		id: 'dtac.queryBuilder.errors.buildQuery.ai-non-final',
		defaultMessage: 'Build query',
		description:
			'Query Builder buildQuery feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	queryName: {
		id: 'dtac.queryBuilder.errors.queryName.ai-non-final',
		defaultMessage: 'Enter a query name.',
		description:
			'Query Builder queryName feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	forgeApp: {
		id: 'dtac.queryBuilder.errors.forgeApp.ai-non-final',
		defaultMessage: 'Select a Forge app.',
		description:
			'Query Builder forgeApp feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
	invalidApp: {
		id: 'dtac.queryBuilder.errors.invalidApp.ai-non-final',
		defaultMessage: 'Select an available Forge app.',
		description:
			'Query Builder invalidApp feedback. Support is the support destination; requestId identifies the failed request when supplied.',
	},
});

export class QueryBuilderError extends Error {
	constructor(
		public readonly kind: QueryBuilderErrorKind,
		public readonly requestId?: string,
	) {
		super(kind);
		this.name = 'QueryBuilderError';
	}
}

export function formatQueryBuilderError(
	intl: IntlShape,
	kind: QueryBuilderErrorKind,
	requestId?: string,
): string {
	const withId =
		requestId && (kind === 'query' || kind === 'siteContext' || kind === 'sites')
			? (`${kind}WithId` as QueryBuilderErrorKind)
			: kind;
	return intl.formatMessage(queryBuilderErrorMessages[withId], {
		support: QUERY_BUILDER_SUPPORT,
		requestId: requestId ?? '',
	});
}

export function queryBuilderExecutionError(
	intl: IntlShape,
	message: string,
	status?: number,
	requestId?: string,
	name?: string,
): { title: string; description: string } {
	if (message.startsWith('Semantic Error')) {
		return { title: formatQueryBuilderError(intl, 'ideTitle'), description: message };
	}
	let kind: QueryBuilderErrorKind = 'query';
	if (
		/Query context must not be null|Can't run Flock queries without any graph keys|ARI provided does not match X-Query-Context header/i.test(
			message,
		)
	)
		kind = 'siteContext';
	else if (status === 429) kind = 'rateLimit';
	else if (name === 'TimeoutError' || name === 'AbortError' || status === 408 || status === 504)
		kind = 'timeout';
	return {
		title: formatQueryBuilderError(intl, 'errorTitle'),
		description: formatQueryBuilderError(intl, kind, requestId),
	};
}
